import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook for scroll-based animations
 * Returns whether the element is visible and scroll progress
 */
export const useScrollAnimation = (threshold = 0.1) => {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold }
    );

    const currentElement = elementRef.current;
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [threshold]);

  useEffect(() => {
    const handleScroll = () => {
      if (elementRef.current) {
        const rect = elementRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const elementHeight = rect.height;
        
        // Calculate progress based on element position in viewport
        const elementTop = rect.top;
        const elementBottom = rect.bottom;
        
        if (elementBottom > 0 && elementTop < windowHeight) {
          const visibleHeight = Math.min(elementBottom, windowHeight) - Math.max(elementTop, 0);
          const progress = visibleHeight / (elementHeight + windowHeight);
          setScrollProgress(Math.max(0, Math.min(1, progress)));
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial calculation

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return { elementRef, isVisible, scrollProgress };
};

/**
 * Custom hook for parallax effect
 * Returns a ref and transform style
 */
export const useParallax = (speed = 0.5) => {
  const [offsetY, setOffsetY] = useState(0);
  const elementRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (elementRef.current) {
        const rect = elementRef.current.getBoundingClientRect();
        const scrolled = window.pageYOffset;
        const elementTop = rect.top + scrolled;
        const windowHeight = window.innerHeight;
        
        // Only apply parallax when element is in viewport
        if (rect.top < windowHeight && rect.bottom > 0) {
          const offset = (scrolled - elementTop + windowHeight) * speed;
          setOffsetY(offset);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial calculation

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [speed]);

  return {
    ref: elementRef,
    style: {
      transform: `translateY(${offsetY}px)`,
    },
  };
};
